import { useCallback, useEffect, useReducer } from "react";
import { CreateColumnsWithDataPayload, ReportColumn, ReportConfig, ReportElement, ReportParams, ReportTableData, Sorter } from "../../components/Reports/types";
import { useQuery, useQueryClient } from "react-query";
import axios from 'ps-components/lib/apiClient';
import { useAppState } from "../../hooks/redux";
import { deepEqual } from "../../lib/utils";
import { useReportSocket } from "./use-report-socket";

type ReportWriterState = {
    config: ReportConfig | undefined
    elements: ReportElement[]
    columns: ReportColumn[]
    sorters: Sorter[]
    tableData: ReportTableData
    page: number
    limit: number
    selectedElements: ReportElement[]
    selectedColumns: ReportColumn[]
    creatingColumnsWithData: boolean
    dataElementsModalOpen: boolean
    filtersModalOpen: boolean
}

type ReportWriterAction =
  | { type: 'loadConfigWithData', payload: ReportConfig }
  | { type: 'reportDataLoaded', payload: ReportTableData }
  | { type: 'setTableData', payload: ReportTableData }
  | { type: 'updateReportParams', payload: Partial<ReportParams> }
  | { type: 'setElements', payload: ReportElement[] }
  | { type: 'setSelectedElements', payload: ReportElement[] }
  | { type: 'resetSelectedElements' }
  | { type: 'setSelectedColumns', payload: ReportColumn[] }
  | { type: 'createColumnsWithData', payload: ReportTableData }
  | { type: 'creatingColumnsWithData', payload: boolean }
  | { type: 'createColumnsWithDataSuccess' }
  | { type: 'setPage', payload: number }
  | { type: 'setLimit', payload: number }
  | { type: 'setSorters', payload: Sorter[] }
  | { type: 'setDataElementsModalOpen', payload: boolean }
  | { type: 'setFiltersModalOpen', payload: boolean }
  | { type: 'moveElement'; dragIndex: number; hoverIndex: number };

const initialState: ReportWriterState = {
    config: undefined,
    elements: [],
    columns: [],
    tableData: {
        rowCount: '',
        rows: [],
        columns: [],
        fields: [],
        sorters: [],
        loadReqTimestamp: 0
    },
    sorters: [],
    page: 1,
    limit: 100,
    selectedElements: [],
    selectedColumns: [],
    creatingColumnsWithData: false,
    dataElementsModalOpen: false,
    filtersModalOpen: false,
}

const reducer = (state: ReportWriterState, action: ReportWriterAction): ReportWriterState => {
    switch (action.type) {
      case 'loadConfigWithData':
        const { elements, columns } = action.payload
        return { ...state, config: action.payload, elements, selectedElements: elements, columns, selectedColumns: columns  };
      case 'reportDataLoaded':
        return { ...state, tableData: action.payload, sorters: action.payload.sorters || []  };
      case 'setTableData':
        return { ...state, tableData: {...state.tableData, ...action.payload } };
      case 'updateReportParams':
        return !state.config ? state : { ...state, config: { ...state.config, params: { ...state.config.params, ...action.payload } } }
      case 'setElements':
        return { ...state, elements: action.payload };
      case 'setSelectedElements':
        return { ...state, selectedElements: action.payload };
      case 'setSelectedColumns':
        return { ...state, selectedColumns: action.payload };
      case 'resetSelectedElements':
          return { ...state, selectedElements: state.elements };
      case 'createColumnsWithData':
        return { ...state, tableData: { ...state.tableData, rows: action.payload.rows, columns: action.payload.columns, fields: action.payload.fields  }, elements: state.selectedElements, columns: action.payload.columns, sorters: action.payload.sorters, page: 1 };
      case 'creatingColumnsWithData':
        return { ...state, creatingColumnsWithData: action.payload };
      case 'createColumnsWithDataSuccess':
        return { ...state, creatingColumnsWithData: false, dataElementsModalOpen: false };
      case 'setPage':
        return { ...state, page: action.payload };
      case 'setLimit':
        return { ...state, limit: action.payload, page: 1 };
      case 'setSorters':
        return { ...state, sorters: action.payload };
      case 'setDataElementsModalOpen':
        return { ...state, dataElementsModalOpen: action.payload };
      case 'setFiltersModalOpen':
        return { ...state, filtersModalOpen: action.payload };
      case 'moveElement':
        const { dragIndex, hoverIndex } = action;
        const draggedItem = state.selectedElements[dragIndex];
        const updatedItems = [...state.selectedElements];
        updatedItems.splice(dragIndex, 1);
        updatedItems.splice(hoverIndex, 0, draggedItem);
        return { ...state, selectedElements: updatedItems };

      default:
        return state;
    }
};

export const useReport = (reportPk: number) => {
    const queryClient = useQueryClient()
    const appState = useAppState()
    const { csrf_timestamp, csrf_token } = appState.session
    const [state, dispatch] = useReducer(reducer, initialState)
    const { config, page, limit, elements, columns, sorters, selectedElements, selectedColumns } = state

    const { socketOpen, reportNotFound } = useReportSocket(reportPk, {
      onReportConfigLoaded: (message) => dispatch({ type: 'loadConfigWithData', payload: message }),
      onReportDataLoaded: (message) => {
        queryClient.setQueryData(['report', { reportPk, page: 1 }], () => ({ config, ...message }))
        dispatch({ type: 'reportDataLoaded', payload: message })
      }
    })

    const loadData = async () => {
        const payload: CreateColumnsWithDataPayload = {
            report_pk: Number(reportPk),
            category_pk: Number(config?.category_pk),
            elements: JSON.stringify(elements),
            filters: JSON.stringify(config?.filters || []),
            params: JSON.stringify(config?.params),
            columns: JSON.stringify(columns),
            markers: config?.markers || { comparatorGroups: [] },
            comparators: config?.comparators || { comparatorGroups: [], baselineComparison: false },
            page,
            start: (page - 1) * limit,
            limit: limit,
            sort: sorters,
            loadReqTimestamp: Date.now(),
            csrf_token: csrf_token,
            csrf_timestamp
        }

        const response = await axios.apiPost(`/api/report-writer/query/loadData?_dc=${csrf_timestamp}`, payload)

        dispatch({ type: 'setTableData', payload: response.data })

        return response.data
    }

    const { isLoading: reportLoading, refetch: refetchLoadData, isPreviousData } = useQuery(['report', { reportPk, page, limit, sorters, elements, columns }], loadData, { enabled: !!elements && !!config?.category_pk, keepPreviousData: true, refetchOnWindowFocus: false })

    const createColumns = async () => {
        const response = await axios.apiPost('/api/report-writer/query/createColumns', { elements: selectedElements, columns })
        dispatch({ type: 'setSelectedColumns', payload: response.data.columns })
    }

    const createColumnsWithData = async () => {
        dispatch({ type: 'creatingColumnsWithData', payload: true })
        try {
            const payload: CreateColumnsWithDataPayload = {
                report_pk: Number(reportPk),
                category_pk: Number(config?.category_pk),
                elements: JSON.stringify(selectedElements),
                filters: JSON.stringify([]),
                params: JSON.stringify(config?.params),
                columns: JSON.stringify(selectedColumns),
                markers: config?.markers || { comparatorGroups: [] },
                comparators: config?.comparators || { comparatorGroups: [], baselineComparison: false },
                page: 1,
                start: 0,
                limit,
                loadReqTimestamp: Date.now(),
                csrf_token,
                csrf_timestamp
            }

            const response = await axios.apiPost(`/api/report-writer/query/createColumnsWithData`, payload)
            queryClient.invalidateQueries('elements')

            dispatch({ type: 'createColumnsWithData', payload: response.data })
        } catch (error) {
            console.log('Create columns with data error: ', error)
        } finally {
            dispatch({ type: 'createColumnsWithDataSuccess' })
        }
    }

    const getReportElements = async () => {
        const categoryTags: any[] = []
        const url = `/api/report-writer/element-tree/lookup?_dc=${csrf_timestamp}&category_pk=${config?.category_pk}&reportElements=true&categoryTags=${categoryTags}&report_pk=${reportPk}&analyticsAggMode=false&node=root&csrf_token=${csrf_token}&csrf_timestamp=${csrf_timestamp}`
        const response = await axios.apiGet(url);
        return response.data
    }

    const { data: elementTree, isLoading: elementTreeLoading } = useQuery(['elements', { categoryPk: config?.category_pk, reportPk, params: config?.params }], getReportElements, { staleTime: Infinity, enabled: !!config?.category_pk })

    const setSelectedElements = (elements: ReportElement[]) => dispatch({ type: 'setSelectedElements', payload: elements })

    const toggleElementSelection = ({ checked, element }: { checked: boolean, element: ReportElement }) => {
        if (checked) {
            setSelectedElements([...selectedElements, element])
        } else {
            setSelectedElements(selectedElements.filter(el => el.text !== element.text))
        }
    }

    const removeElement = (element: ReportElement) => {
        setSelectedElements(selectedElements.filter(el => element.field_code ? el.field_code !== element.field_code : el.field_pk !== element.field_pk))
    }

    const resetSelectedElements = () => setSelectedElements(elements)
    const setPage = (page: number) => dispatch({ type: 'setPage', payload: page })
    const selectLimit = (limit: string | null) => dispatch({ type: 'setLimit', payload: Number(limit) })
    const setSorters = (sorters: Sorter[]) => dispatch({ type: 'setSorters', payload: sorters || []})
    const setDataElementsModalOpen = (open: boolean) => dispatch({ type: 'setDataElementsModalOpen', payload: open })
    const setFiltersModalOpen = (open: boolean) => dispatch({ type: 'setFiltersModalOpen', payload: open })
    const updateReportParams = (params: Partial<ReportParams>) => dispatch({ type: 'updateReportParams', payload: params })
    const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
      dispatch({ type: 'moveElement', dragIndex, hoverIndex });
    }, []);

    useEffect(() => {
        if (!deepEqual(state.elements, state.selectedElements)) {
            createColumns()
        }
    }, [state.elements, state.selectedElements])

    useEffect(() => {
      if (state.config?.params) {
        loadData()
      }
    }, [
      state.config?.params?.monetary_rounding,
      state.config?.params?.percent_rounding,
      state.config?.params?.numeric_rounding,
      state.config?.params?.salary_display_code,
    ])

    return {
        state,
        socketOpen,
        reportNotFound,
        reportLoading,
        elementTreeLoading,
        elementTree,
        sorters,
        isPreviousData,
        actions: {
          setPage,
          selectLimit,
          setSelectedElements,
          setSorters,
          resetSelectedElements,
          createColumns,
          createColumnsWithData,
          toggleElementSelection,
          removeElement,
          setDataElementsModalOpen,
          setFiltersModalOpen,
          moveItem,
          updateReportParams,
          loadData: refetchLoadData
        }
    }
}
